import React, { useMemo, useEffect, useState } from 'react';
import { Flame, RefreshCw } from 'lucide-react';

/**
 * EngagementScore - Daily gamified score (0-100)
 * Now fetches from /api/dashboard/engagement for real-time data
 */
const EngagementScore = ({ analytics, chatbotId, userId }) => {
    const [engagementData, setEngagementData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Fetch from engagement endpoint
    useEffect(() => {
        const fetchEngagement = async () => {
            if (!chatbotId && !userId) return;

            try {
                setLoading(true);
                const params = new URLSearchParams();
                if (chatbotId) params.append('chatbotId', chatbotId);
                if (userId) params.append('userId', userId);

                const response = await fetch(`/api/dashboard/engagement?${params}`);
                if (response.ok) {
                    const data = await response.json();
                    setEngagementData(data.engagement);
                }
            } catch (error) {
                console.error('Failed to fetch engagement:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchEngagement();
    }, [chatbotId, userId]);

    // Fallback to calculated score if endpoint not available
    const scoreBreakdown = useMemo(() => {
        if (engagementData) {
            return {
                total: engagementData.score,
                breakdown: {
                    containment: {
                        score: engagementData.breakdown.containment.score,
                        max: 40,
                        label: 'Self-Service',
                        value: `${engagementData.breakdown.containment.rate?.toFixed(0) || 0}%`
                    },
                    efficiency: {
                        score: engagementData.breakdown.efficiency.score,
                        max: 25,
                        label: 'Efficiency',
                        value: engagementData.breakdown.efficiency.avgMessages > 0
                            ? `${engagementData.breakdown.efficiency.avgMessages.toFixed(1)} msgs`
                            : '-'
                    },
                    coverage: {
                        score: engagementData.breakdown.coverage.score,
                        max: 20,
                        label: 'Coverage',
                        value: `${engagementData.breakdown.coverage.conversations} convos`
                    },
                    health: {
                        score: engagementData.breakdown.health.score,
                        max: 15,
                        label: 'Queue Health',
                        value: `${engagementData.breakdown.health.openTickets} open`
                    }
                }
            };
        }

        // Fallback calculation from analytics prop
        const conversations = analytics?.messageVolume?.totalConversations || 0;
        const openTickets = analytics?.ticketMetrics?.openTickets || 0;
        const resolvedTickets = analytics?.ticketMetrics?.resolvedTickets || 0;
        const avgMessages = analytics?.conversationMetrics?.avgMessagesPerConversation || 0;
        const totalTickets = openTickets + resolvedTickets;

        const containmentRate = totalTickets > 0 ? Math.min((resolvedTickets / totalTickets) * 100, 100) : 100;
        const containmentScore = (containmentRate / 100) * 40;
        const efficiencyScore = avgMessages > 0 ? Math.max(25 - (avgMessages - 3) * 3, 0) : 25;
        const responseCoverage = conversations > 0 ? 20 : 0;
        const ticketHealth = openTickets <= 5 ? 15 : Math.max(15 - (openTickets / 50) * 15, 0);
        const totalScore = Math.round(containmentScore + efficiencyScore + responseCoverage + ticketHealth);

        return {
            total: Math.min(totalScore, 100),
            breakdown: {
                containment: { score: containmentScore, max: 40, label: 'Self-Service', value: `${containmentRate.toFixed(0)}%` },
                efficiency: { score: efficiencyScore, max: 25, label: 'Efficiency', value: avgMessages > 0 ? `${avgMessages.toFixed(1)} msgs` : '-' },
                coverage: { score: responseCoverage, max: 20, label: 'Coverage', value: `${conversations} convos` },
                health: { score: ticketHealth, max: 15, label: 'Queue Health', value: `${openTickets} open` }
            }
        };
    }, [analytics, engagementData]);

    const streak = engagementData?.streak || { current: 0 };
    const challenges = engagementData?.challenges || [
        { id: 1, label: 'Resolve pending tickets', target: 5, current: 2 },
        { id: 2, label: 'Maintain 70+ score', target: 7, current: 0 }
    ];

    const getGrade = (score) => {
        if (score >= 90) return { grade: 'A+', label: 'Exceptional', color: '#10b981' };
        if (score >= 80) return { grade: 'A', label: 'Excellent', color: '#10b981' };
        if (score >= 70) return { grade: 'B', label: 'Good', color: '#3b82f6' };
        if (score >= 60) return { grade: 'C', label: 'Fair', color: '#f59e0b' };
        return { grade: 'D', label: 'Needs Improvement', color: '#ef4444' };
    };

    const grade = getGrade(scoreBreakdown.total);

    return (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    Engagement Score
                    {loading && <RefreshCw size={10} className="animate-spin text-slate-400" />}
                </h3>
                {streak.current > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-orange-600 font-medium">
                        <Flame size={12} />
                        <span>{streak.current} day streak</span>
                    </div>
                )}
            </div>

            {/* Score Display */}
            <div className="flex items-center gap-6 mb-5">
                <div className="relative w-24 h-24">
                    <svg className="w-24 h-24 transform -rotate-90">
                        <circle cx="48" cy="48" r="40" fill="none" stroke="#f1f5f9" strokeWidth="8" />
                        <circle
                            cx="48" cy="48" r="40" fill="none"
                            stroke={grade.color}
                            strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray={`${(scoreBreakdown.total / 100) * 251.2} 251.2`}
                            className="transition-all duration-700"
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold text-slate-800">{scoreBreakdown.total}</span>
                    </div>
                </div>

                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg font-bold" style={{ color: grade.color }}>{grade.grade}</span>
                        <span className="text-sm text-slate-600">{grade.label}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                        {scoreBreakdown.total >= 70
                            ? "Your chatbot is performing well."
                            : "Focus on reducing open tickets to improve."
                        }
                    </p>
                </div>
            </div>

            {/* Breakdown */}
            <div className="grid grid-cols-4 gap-2 py-4 border-t border-slate-100">
                {Object.entries(scoreBreakdown.breakdown).map(([key, data]) => {
                    const percent = (data.score / data.max) * 100;
                    const barColor = percent >= 70 ? '#10b981' : percent >= 40 ? '#f59e0b' : '#ef4444';
                    return (
                        <div key={key} className="text-center">
                            <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">{data.label}</div>
                            <div className="text-sm font-semibold text-slate-700">{Math.round(data.score)}/{data.max}</div>
                            <div className="h-1 bg-slate-100 rounded-full mt-1 overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${percent}%`, backgroundColor: barColor }} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Goals */}
            <div className="pt-4 border-t border-slate-100">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">Active Goals</div>
                <div className="space-y-2">
                    {challenges.slice(0, 2).map((challenge) => {
                        const progress = (challenge.current / challenge.target) * 100;
                        return (
                            <div key={challenge.id}>
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="text-slate-600">{challenge.label}</span>
                                    <span className="text-slate-400 tabular-nums">{challenge.current}/{challenge.target}</span>
                                </div>
                                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-violet-500 rounded-full transition-all"
                                        style={{ width: `${Math.min(progress, 100)}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default EngagementScore;
